// src/redux/discoverySlice.js
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getDiscoveryFeed } from "../services/discoveryService";

import {
  DEFAULT_CITY_SLUG,
  DEFAULT_NICHE_SLUG,
} from "../data/slugIndex";
import {
  getLaunchNicheMode,
  resolveDiscoveryCitySlug,
  resolveDiscoveryNicheSlug,
} from "../utils/discoverySegment";

const DEFAULT_PLATFORM = "instagram";

function safeStr(value) {
  return String(value ?? "")
    .replace(/[\u0000-\u001F\u007F]/g, "")
    .trim();
}

function lower(value) {
  return safeStr(value).toLowerCase();
}

function firstArrayValue(value) {
  return Array.isArray(value) && value.length ? value[0] : undefined;
}

function firstNonEmpty(...values) {
  for (const value of values) {
    if (Array.isArray(value)) {
      const first = firstArrayValue(value);
      if (safeStr(first)) return first;
      continue;
    }

    if (safeStr(value)) return value;
  }

  return undefined;
}

function normalizeView(value) {
  const view = lower(value);
  return view === "creators" || view === "trending" ? view : "feed";
}

function normalizeBool(value, fallback = true) {
  if (typeof value === "boolean") return value;
  if (value === undefined || value === null || value === "") return fallback;

  const text = lower(value);

  if (["true", "1", "yes", "y", "on"].includes(text)) return true;
  if (["false", "0", "no", "n", "off"].includes(text)) return false;

  return fallback;
}

function normalizePlatform() {
  // Launch discovery is Instagram-only for now.
  return DEFAULT_PLATFORM;
}

function normalizeNicheMode(value) {
  const mode = lower(value).replace("-", "_");
  return mode === "parent" || mode === "child" ? mode : "";
}

function normalizeCity(value, fallback = DEFAULT_CITY_SLUG) {
  return resolveDiscoveryCitySlug(value, fallback);
}

function normalizeNiche(value, fallback = DEFAULT_NICHE_SLUG) {
  return resolveDiscoveryNicheSlug(value, fallback);
}

function getPreferencesFromState(state) {
  return (
    state?.preferences?.preferences ||
    state?.preferences?.data ||
    state?.preferences?.item ||
    state?.preferences ||
    {}
  );
}

function getCityFromPreferences(preferences = {}) {
  const feedDefaults = preferences.feedDefaults || preferences.feed_defaults || {};

  return firstNonEmpty(
    feedDefaults.city,
    feedDefaults.citySlug,
    feedDefaults.city_slug,
    preferences.activeCitySlug,
    preferences.active_city_slug,
    preferences.selectedCitySlug,
    preferences.selected_city_slug,
    preferences.primaryCitySlug,
    preferences.primary_city_slug,
    preferences.citySlug,
    preferences.city_slug,
    preferences.locationSlug,
    preferences.location_slug,
    preferences.location,
    preferences.city,
    firstArrayValue(preferences.cities),
    firstArrayValue(preferences.citySelections)?.slug,
    firstArrayValue(preferences.city_selections)?.slug
  );
}

function getNicheFromPreferences(preferences = {}) {
  const feedDefaults = preferences.feedDefaults || preferences.feed_defaults || {};

  return firstNonEmpty(
    feedDefaults.niche,
    feedDefaults.nicheSlug,
    feedDefaults.niche_slug,
    preferences.activeNicheSlug,
    preferences.active_niche_slug,
    preferences.selectedNicheSlug,
    preferences.selected_niche_slug,
    preferences.primaryNicheSlug,
    preferences.primary_niche_slug,
    preferences.preferredNicheSlug,
    preferences.preferred_niche_slug,
    preferences.nicheSlug,
    preferences.niche_slug,
    preferences.niche,
    preferences.interests,
    preferences.industry,
    firstArrayValue(preferences.niches),
    firstArrayValue(preferences.nicheSelections)?.slug,
    firstArrayValue(preferences.niche_selections)?.slug
  );
}

function getNicheModeFromPreferences(preferences = {}, nicheSlug = "") {
  const feedDefaults = preferences.feedDefaults || preferences.feed_defaults || {};

  const nicheModeBySlug =
    preferences.nicheModeBySlug || preferences.niche_mode_by_slug || {};

  const fromMap = nicheModeBySlug?.[nicheSlug];

  const selectedNiche =
    preferences.selectedNiche ||
    preferences.selected_niche ||
    preferences.primaryNiche ||
    preferences.primary_niche ||
    firstArrayValue(preferences.nicheSelections) ||
    firstArrayValue(preferences.niche_selections) ||
    {};

  return firstNonEmpty(
    feedDefaults.nicheMode,
    feedDefaults.niche_mode,
    fromMap,
    selectedNiche?.mode,
    selectedNiche?.nicheMode,
    selectedNiche?.niche_mode,
    preferences.nicheMode,
    preferences.niche_mode,
    firstArrayValue(preferences.nicheModes),
    firstArrayValue(preferences.niche_modes)
  );
}

function normalizeDiscoveryParams(params = {}, preferences = {}) {
  const feedDefaults = preferences.feedDefaults || preferences.feed_defaults || {};

  const cityRaw = firstNonEmpty(
    params.city,
    params.citySlug,
    params.city_slug,
    params.locationSlug,
    params.location_slug,
    params.location,
    params.citySelection?.slug,
    params.selectedCity?.slug,
    firstArrayValue(params.cities),
    getCityFromPreferences(preferences),
    feedDefaults.city
  );

  const nicheRaw = firstNonEmpty(
    params.niche,
    params.nicheSlug,
    params.niche_slug,
    params.preferredNicheSlug,
    params.preferred_niche_slug,
    params.selectedNiche?.slug,
    params.nicheSelection?.slug,
    params.interests,
    params.industry,
    firstArrayValue(params.niches),
    getNicheFromPreferences(preferences),
    feedDefaults.niche
  );

  const city = normalizeCity(cityRaw, DEFAULT_CITY_SLUG);
  const niche = normalizeNiche(nicheRaw, DEFAULT_NICHE_SLUG);

  const explicitMode = firstNonEmpty(
    params.nicheMode,
    params.niche_mode,
    params.mode,
    params.selectedNiche?.mode,
    params.nicheSelection?.mode,
    getNicheModeFromPreferences(preferences, niche)
  );

  const nicheMode = getLaunchNicheMode(niche, explicitMode);

  return {
    ...params,
    platform: normalizePlatform(params.platform),
    city,
    niche,
    nicheMode,
    niche_mode: nicheMode,
    view: normalizeView(params.view),
    excludeSeen: normalizeBool(
      params.excludeSeen ?? params.exclude_seen,
      true
    ),
    exclude_seen: normalizeBool(
      params.excludeSeen ?? params.exclude_seen,
      true
    ),
  };
}

export const fetchDiscoveryFeedAsync = createAsyncThunk(
  "discovery/fetchFeed",
  async (params, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const preferences = getPreferencesFromState(state);
      const requestParams = normalizeDiscoveryParams(params || {}, preferences);

      const payload = await getDiscoveryFeed(requestParams);
      return {
        payload: payload || {},
        requestParams,
      };
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to load discovery feed.");
    }
  }
);

const getCreatorId = (creator) =>
  String(
    creator?.external_id ||
      creator?.externalId ||
      creator?.id ||
      creator?.channelId ||
      creator?.channel_id ||
      creator?.keyId ||
      creator?._routeId ||
      creator?.username ||
      ""
  ).trim();

const dedupeById = (items) => {
  const seen = new Set();
  const out = [];

  for (const item of Array.isArray(items) ? items : []) {
    const id = getCreatorId(item);
    if (!id) continue;

    const key = id.toLowerCase();
    if (seen.has(key)) continue;

    seen.add(key);
    out.push(item);
  }

  return out;
};

const dedupeAcrossSections = ({
  recommended = [],
  rising = [],
  active = [],
  similar = [],
}) => {
  const used = new Set();

  const take = (items) =>
    (Array.isArray(items) ? items : []).filter((item) => {
      const id = getCreatorId(item);
      if (!id) return false;

      const key = id.toLowerCase();
      if (used.has(key)) return false;

      used.add(key);
      return true;
    });

  return {
    recommended: take(recommended),
    rising: take(rising),
    active: take(active),
    similar: take(similar),
  };
};

const normalizeTags = (tags) => {
  const seen = new Set();

  return (Array.isArray(tags) ? tags : []).filter((tag) => {
    const key = String(tag?.tag || tag?.name || tag || "")
      .trim()
      .toLowerCase();

    if (!key || seen.has(key)) return false;

    seen.add(key);
    return true;
  });
};

const normalizeResponseSegment = (segment, requestParams) => {
  const src = segment && typeof segment === "object" ? segment : {};

  const city = normalizeCity(src.city || src.city_slug || requestParams.city);

  const niche = normalizeNiche(
    src.niche || src.niche_slug || requestParams.niche
  );

  const backendMode = firstNonEmpty(
    src.nicheMode,
    src.niche_mode,
    requestParams.nicheMode,
    requestParams.niche_mode
  );

  const nicheMode = getLaunchNicheMode(niche, backendMode);

  const recommendedNiche = normalizeNiche(
    src.recommendedNiche ||
      src.recommended_niche ||
      src.recommendedNicheSlug ||
      src.recommended_niche_slug ||
      niche,
    niche
  );

  const recommendedBackendMode = firstNonEmpty(
    src.recommendedNicheMode,
    src.recommended_niche_mode,
    nicheMode
  );

  const recommendedNicheMode = getLaunchNicheMode(
    recommendedNiche,
    recommendedBackendMode
  );

  return {
    ...src,
    platform: DEFAULT_PLATFORM,
    city,
    niche,
    nicheMode,
    niche_mode: nicheMode,
    recommendedNiche,
    recommended_niche: recommendedNiche,
    recommendedNicheMode,
    recommended_niche_mode: recommendedNicheMode,
  };
};

const emptySections = {
  trendingHashtags: [],
  recommendedCreators: [],
  risingCreators: [],
  mostActiveCreators: [],
  similarCreators: [],
};

const initialState = {
  loading: false,
  error: null,

  generatedAt: null,

  meta: {
    view: "feed",
    segment: null,
    lockViews: true,
    plan: null,
    excludeSeenApplied: true,
    preferencesApplied: false,
    preferencesVersion: "none",
  },

  trendingHashtags: [],
  recommendedCreators: [],
  risingCreators: [],
  mostActiveCreators: [],
  similarCreators: [],

  sections: { ...emptySections },

  lastFetchedAt: 0,
  lastFetchedAtByView: {
    feed: 0,
    creators: 0,
    trending: 0,
  },

  currentRequestId: null,
  lastRequestParams: null,
};

const discoverySlice = createSlice({
  name: "discovery",
  initialState,
  reducers: {
    clearDiscoveryError(state) {
      state.error = null;
    },

    clearDiscovery(state) {
      state.loading = false;
      state.error = null;
      state.generatedAt = null;

      state.meta = {
        ...initialState.meta,
        segment: null,
      };

      state.trendingHashtags = [];
      state.recommendedCreators = [];
      state.risingCreators = [];
      state.mostActiveCreators = [];
      state.similarCreators = [];

      state.sections = { ...emptySections };

      state.lastFetchedAt = 0;
      state.lastFetchedAtByView = {
        feed: 0,
        creators: 0,
        trending: 0,
      };

      state.currentRequestId = null;
      state.lastRequestParams = null;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchDiscoveryFeedAsync.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.currentRequestId = action.meta.requestId;
      })

      .addCase(fetchDiscoveryFeedAsync.fulfilled, (state, action) => {
        if (state.currentRequestId !== action.meta.requestId) return;

        state.loading = false;
        state.currentRequestId = null;

        const payload = action.payload?.payload || {};
        const requestParams =
          action.payload?.requestParams ||
          normalizeDiscoveryParams(action?.meta?.arg || {});

        state.lastRequestParams = requestParams;

        const view = normalizeView(
          payload?.meta?.view || requestParams?.view || "feed"
        );

        const now = Date.now();
        state.lastFetchedAt = now;
        state.lastFetchedAtByView[view] = now;

        state.generatedAt =
          payload.generatedAt ||
          payload.generated_at ||
          payload.generated_at_iso ||
          null;

        const meta = payload.meta || {};

        state.meta = {
          view,
          segment: normalizeResponseSegment(meta.segment, requestParams),
          lockViews:
            typeof meta.lockViews === "boolean"
              ? meta.lockViews
              : state.meta.lockViews,
          plan: meta.plan ?? null,
          excludeSeenApplied:
            typeof meta.excludeSeenApplied === "boolean"
              ? meta.excludeSeenApplied
              : typeof meta.exclude_seen_applied === "boolean"
                ? meta.exclude_seen_applied
                : state.meta.excludeSeenApplied,
          preferencesApplied:
            typeof meta.preferencesApplied === "boolean"
              ? meta.preferencesApplied
              : typeof meta.preferences_applied === "boolean"
                ? meta.preferences_applied
                : state.meta.preferencesApplied,
          preferencesVersion:
            meta.preferencesVersion || meta.preferences_version || "none",
          visibility: meta.visibility || state.meta.visibility,
        };

        const tags =
          payload.trendingHashtags ||
          payload.trending_hashtags ||
          payload?.sections?.trendingHashtags ||
          payload?.sections?.trending_hashtags ||
          [];

        const recommendedRaw =
          payload.recommendedCreators ||
          payload.recommended_creators ||
          payload.forYouCreators ||
          payload.for_you_creators ||
          payload?.sections?.recommendedCreators ||
          payload?.sections?.recommended_creators ||
          payload?.sections?.forYouCreators ||
          payload?.sections?.for_you_creators ||
          [];

        const risingRaw =
          payload.risingCreators ||
          payload.rising_creators ||
          payload?.sections?.risingCreators ||
          payload?.sections?.rising_creators ||
          [];

        const activeRaw =
          payload.mostActiveCreators ||
          payload.most_active_creators ||
          payload?.sections?.mostActiveCreators ||
          payload?.sections?.most_active_creators ||
          [];

        const similarRaw =
          payload.similarCreators ||
          payload.similar_creators ||
          payload?.sections?.similarCreators ||
          payload?.sections?.similar_creators ||
          [];

        const trendingHashtags = normalizeTags(tags);

        const recommended = dedupeById(recommendedRaw);
        const rising = dedupeById(risingRaw);
        const active = dedupeById(activeRaw);
        const similar = dedupeById(similarRaw);

        const deduped = dedupeAcrossSections({
          recommended,
          rising,
          active,
          similar,
        });

        state.trendingHashtags = trendingHashtags;
        state.recommendedCreators = deduped.recommended;
        state.risingCreators = deduped.rising;
        state.mostActiveCreators = deduped.active;
        state.similarCreators = deduped.similar;

        state.sections = {
          trendingHashtags: state.trendingHashtags,
          recommendedCreators: state.recommendedCreators,
          risingCreators: state.risingCreators,
          mostActiveCreators: state.mostActiveCreators,
          similarCreators: state.similarCreators,
        };
      })

      .addCase(fetchDiscoveryFeedAsync.rejected, (state, action) => {
        if (state.currentRequestId !== action.meta.requestId) return;

        state.loading = false;
        state.currentRequestId = null;
        state.error =
          action.payload ||
          action.error?.message ||
          "Failed to load discovery feed.";
      });
  },
});

export const { clearDiscoveryError, clearDiscovery } = discoverySlice.actions;

export const selectDiscoveryLoading = (state) =>
  state.discovery?.loading || false;

export const selectDiscoveryError = (state) => state.discovery?.error || null;

export const selectDiscoveryGeneratedAt = (state) =>
  state.discovery?.generatedAt || null;

export const selectDiscoveryMeta = (state) =>
  state.discovery?.meta || initialState.meta;

export const selectDiscoverySegment = (state) =>
  state.discovery?.meta?.segment || null;

export const selectDiscoveryLastRequestParams = (state) =>
  state.discovery?.lastRequestParams || null;

export const selectDiscoveryLastFetchedAt = (state) =>
  state.discovery?.lastFetchedAt || 0;

export const selectDiscoveryLastFetchedAtByView = (state, view = "feed") =>
  state.discovery?.lastFetchedAtByView?.[normalizeView(view)] || 0;

export const selectDiscoverySections = (state) =>
  state.discovery?.sections || initialState.sections;

export const selectTrendingHashtags = (state) =>
  state.discovery?.trendingHashtags || [];

export const selectRecommendedCreators = (state) =>
  state.discovery?.recommendedCreators || [];

export const selectRisingCreators = (state) =>
  state.discovery?.risingCreators || [];

export const selectMostActiveCreators = (state) =>
  state.discovery?.mostActiveCreators || [];

export const selectSimilarCreators = (state) =>
  state.discovery?.similarCreators || [];

export default discoverySlice.reducer;